// The part material set (WP5): one toon "uber" material per rig/prop instance (its uniforms are
// that instance's look: body color, pattern, identity, expression, flash…), plus hull, shadow-depth
// and ghost-aura materials. Everything is posed by three.js skinning (partGeometry.ts), so a whole
// character is 1 draw + 1 hull draw (+1 shadow-map draw on tiers with shadows). The toon program is
// shared by every instance (fixed `customProgramCacheKey`); the hull and depth materials are shared
// outright, because they read the pose from the object's skeleton, not from uniforms.
import {
  AdditiveBlending, BackSide, Color, MeshDepthMaterial, MeshToonMaterial, RGBADepthPacking, ShaderMaterial,
  type IUniform, type Texture,
} from 'three';
import { BAYER_GLSL, GLOSS_GLSL, GLYPH_GLSL, NOISE_GLSL, PATTERN_GLSL } from './shaders/chunks';
import { INK, libraryShared, singleBoneSkinning, SKIN1, type MaterialLibrary } from './library';

export interface PartUniforms {
  body: IUniform<Color>;
  body2: IUniform<Color>;
  pattern: IUniform<number>;
  ident: IUniform<Color>;
  team: IUniform<Color>;
  /** (expression A, expression B, blend 0..1). */
  expr: IUniform<[number, number, number]>;
  face: IUniform<number>;
  blush: IUniform<number>;
  flush: IUniform<number>;
  flash: IUniform<number>;
  spiral: IUniform<number>;
  danger: IUniform<number>;
  potato: IUniform<number>;
  ghost: IUniform<number>;
  ghostColor: IUniform<Color>;
  glyph: IUniform<number>;
  gloss: IUniform<number>;
  spark: IUniform<number>;
  /** Per-instance phase for twinkles so props do not sparkle in sync. */
  seed: IUniform<number>;
  /** 0..1 fade-out (crate/peel lifetime end): dithered away, no sorting. */
  fade: IUniform<number>;
}

export interface PartMaterials {
  main: MeshToonMaterial;
  /** Additive aura drawn on the hull while the object is a ghost (§12.4 "soft additive glow"). */
  aura: ShaderMaterial;
  u: PartUniforms;
  dispose(): void;
}

const VERTEX_DECL = /* glsl */ `
attribute vec4 aInfo;
attribute vec4 aPat;
varying vec3 vSoObj;
varying vec4 vSoInfo;
varying vec4 vSoPat;
varying vec3 vSoColor2;
vec3 so_unpackColor(float packed) {
  float r = floor(packed / 65536.0);
  float g = floor((packed - r * 65536.0) / 256.0);
  float b = packed - r * 65536.0 - g * 256.0;
  vec3 c = vec3(r, g, b) / 255.0;
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c));
}
`;

const FRAGMENT_DECL = /* glsl */ `
varying vec3 vSoObj;
varying vec4 vSoInfo;
varying vec4 vSoPat;
varying vec3 vSoColor2;
uniform vec3 uRimColor;
uniform float uTime;
uniform vec3 uBody;
uniform vec3 uBody2;
uniform float uPattern;
uniform vec3 uIdent;
uniform vec3 uTeam;
uniform vec3 uExpr;
uniform float uFace;
uniform float uBlush;
uniform float uFlush;
uniform float uFlash;
uniform float uSpiral;
uniform float uDanger;
uniform float uPotato;
uniform float uGhost;
uniform vec3 uGhostColor;
uniform float uGlyph;
uniform float uGloss;
uniform float uSpark;
uniform float uSeed;
uniform float uFade;
#ifdef SO_MOUTH
uniform sampler2D uMouth;
#endif
${NOISE_GLSL}
${PATTERN_GLSL}
${GLYPH_GLSL}
${BAYER_GLSL}

#ifdef SO_MOUTH
vec4 so_mouthCell(vec2 uv, float e) {
  float col = mod(e, 3.0);
  float row = floor(e / 3.0 + 0.01);
  vec2 a = vec2((col + uv.x) / 3.0, 1.0 - (row + 1.0 - uv.y) / 2.0);
  return texture2D(uMouth, a);
}
#endif

vec3 so_face(vec3 p, vec3 base) {
  // Blush (§12.4): pink cheeks at (±0.25, 0.68, 0.38), alpha 0.5, brighter with Dizzy.
  vec3 q = vec3(abs(p.x), p.y, p.z);
  float bd = length((q - vec3(0.25, 0.665, 0.37)) * vec3(1.0, 1.3, 1.0));
  float bw = max(fwidth(bd), 1e-4);
  float blush = (1.0 - smoothstep(0.07 - bw, 0.07 + bw, bd)) * uBlush * step(0.1, p.z);
  base = mix(base, vec3(1.0, 0.33, 0.47), blush);
  #ifdef SO_MOUTH
  if (p.z > 0.2 && uFace > 0.5) {
    vec2 uv = vec2(p.x / 0.3 + 0.5, (p.y - 0.6) / 0.2 + 0.5);
    if (uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0) {
      vec4 a = so_mouthCell(uv, uExpr.x);
      vec4 b = so_mouthCell(uv, uExpr.y);
      vec4 m = mix(a, b, uExpr.z);
      base = mix(base, m.rgb, m.a);
    }
  }
  #endif
  return base;
}
`;

/** Per-mode albedo; sets `soUnlit` (1 = emit the albedo as-is) and the per-fragment gloss. */
const MAIN_COLOR = /* glsl */ `
float soMode = floor(vSoInfo.x + 0.5);
float soUnlit = 0.0;
float soGlossK = uGloss;
vec3 soAlb = diffuseColor.rgb;
float soAlpha = 1.0;
float soEyeLift = 0.0;
vec3 soEmit = vec3(0.0);
float soFaceA = 0.0;
if (soMode < 0.5) {                                   // BODY: pattern, flush, face
  soAlb = uGhost > 0.5 ? uGhostColor : so_pattern(vSoObj, uBody, uBody2, uPattern, uTime);
  soAlb = mix(soAlb, vec3(1.0, 0.24, 0.22), uFlush * 0.38);
  vec3 soBase = soAlb;
  soAlb = so_face(vSoObj, soAlb);
  soFaceA = clamp(length(soAlb - soBase) * 4.0, 0.0, 1.0);
} else if (soMode < 1.5) {                            // VCOLOR
} else if (soMode < 2.5) {                            // TINT: vertex color × body color
  soAlb = diffuseColor.rgb * mix(uBody, vec3(1.0, 0.24, 0.22), uFlush * 0.38);
} else if (soMode < 3.5) {                            // IDENT (lit, glossy)
  soAlb = uIdent * diffuseColor.rgb;
  soGlossK = 1.0;
} else if (soMode < 4.5) {                            // IDENT_UNLIT
  soAlb = uIdent * diffuseColor.rgb;
  soUnlit = 1.0;
} else if (soMode < 5.5) {                            // UNLIT
  soUnlit = 1.0;
} else if (soMode < 6.5) {                            // GLYPH badge (§7.2): cream sticker, identity glyph, ink lines
  vec2 g = vSoPat.yz;
  float r = length(g);
  float w = max(fwidth(r), 1e-3);
  float gd = so_glyph(g * 1.45, uGlyph) / 1.45;
  float gw = max(fwidth(gd), 1e-3);
  vec3 c = vec3(1.0, 0.965, 0.914);
  c = mix(c, vec3(${hexVec(INK)}), 1.0 - smoothstep(0.055 - gw, 0.055 + gw, abs(gd + 0.0)));
  c = mix(c, uIdent, 1.0 - smoothstep(-gw, gw, gd + 0.045));
  c = mix(c, vec3(${hexVec(INK)}), smoothstep(0.84 - w, 0.84 + w, r));
  soAlb = c;
  soUnlit = 1.0;
} else if (soMode < 7.5) {                            // PUPIL: glossy black + two catchlights
  vec3 hp = vSoObj / 0.065;
  float h1 = 1.0 - smoothstep(0.22, 0.3, length(hp.xy - vec2(-0.3, 0.34)));
  float h2 = 1.0 - smoothstep(0.08, 0.14, length(hp.xy - vec2(0.3, -0.26)));
  soAlb = vec3(0.022, 0.016, 0.035) + vec3(1.0) * max(h1, h2 * 0.75) * step(0.0, hp.z);
  soUnlit = 1.0;
} else if (soMode < 8.5) {                            // EYE white (+ Dizzy spirals): reads white in any light
  soAlb = vec3(1.0);
  soGlossK = 0.9;
  soEyeLift = 0.45;
  if (uSpiral > 0.0 && vSoObj.z > 0.0) {
    vec2 q = vSoObj.xy / 0.13;
    float a = atan(q.y, q.x);
    float r = length(q);
    float s = sin(a + r * 15.0 - uTime * 10.0);
    float w = max(fwidth(s), 1e-3) * 1.2;
    float line = smoothstep(0.25 - w, 0.25 + w, s) * (1.0 - smoothstep(0.88, 0.95, r));
    soAlb = mix(soAlb, vec3(0.08, 0.04, 0.12), line * uSpiral);
  }
} else if (soMode < 9.5) {                            // GOLD with glitter
  soGlossK = 1.0;
  vec3 cell = floor(vSoObj * 34.0);
  float h = so_hash13(cell + uSeed);
  float tw = step(0.9, h) * max(0.0, sin(uTime * 5.0 + h * 50.0));
  soAlb += vec3(1.0, 0.95, 0.75) * tw * 0.8;
} else if (soMode < 10.5) {                           // GLASS (vertex tint, alpha base in aPat.y)
  soGlossK = 1.0;
} else if (soMode < 11.5) {                           // TEAM_UNLIT
  soAlb = uTeam;
  soUnlit = 1.0;
} else if (soMode < 12.5) {                           // DANGER (teeter edge glow, stomp target)
  soAlb = diffuseColor.rgb * (0.45 + 0.85 * uDanger);
  soUnlit = 1.0;
} else if (soMode < 13.5) {                           // POTATO / bomb: a dark body with a pulsing red glow
  soEmit = vec3(1.0, 0.12, 0.06) * uPotato * (0.55 + 0.45 * pow(1.0 - abs(dot(normalize(vNormal), normalize(vViewPosition))), 1.5));
  soAlb = mix(diffuseColor.rgb, vec3(0.5, 0.06, 0.05), uPotato * 0.35);
  soGlossK = 1.0;
} else if (soMode < 14.5) {                           // SPARK (lit fuse)
  soAlb = diffuseColor.rgb * (1.1 + uSpark);
  soUnlit = 1.0;
} else if (soMode < 15.5) {                           // DECAL: lit vertex color, no gloss
  soGlossK = 0.0;
} else if (soMode < 16.5) {                           // SUGAR: granular crystal with big twinkling glints
  float n = so_noise3(vSoObj * 18.0 + uSeed);
  soAlb *= 0.9 + 0.12 * n;
  soAlb = mix(soAlb, soAlb * vec3(0.88, 0.92, 1.0), smoothstep(0.3, 0.7, so_noise3(vSoObj * 5.0 - uSeed)));
  vec3 gp = vSoObj * 9.0;
  vec3 gc = floor(gp);
  float h = so_hash13(gc + uSeed * 3.0);
  vec3 gf = fract(gp) - 0.5;
  float phase = pow(max(0.0, sin(uTime * (2.5 + h * 3.0) + h * 60.0)), 8.0);
  float cross = max(smoothstep(0.08, 0.0, abs(gf.x) * 0.5 + length(gf.yz)), smoothstep(0.08, 0.0, abs(gf.y) * 0.5 + length(gf.xz)));
  float glint = step(0.82, h) * phase * max(cross, smoothstep(0.18, 0.0, length(gf)));
  soAlb += vec3(1.0, 0.98, 0.9) * glint * 2.2;
  soGlossK = 0.85;
} else if (soMode < 17.5) {                           // CARDBOARD: fibers + corrugation
  float fib = so_noise3(vSoObj * vec3(60.0, 9.0, 60.0));
  float cor = 0.5 + 0.5 * sin(vSoObj.y * 90.0);
  soAlb *= 0.9 + 0.12 * fib - 0.05 * cor;
  soGlossK = 0.15;
} else {                                              // GLOW (unlit, brightened for bloom)
  soAlb = diffuseColor.rgb * 1.25;
  soUnlit = 1.0;
}
float soPatKind = floor(vSoPat.w + 0.5);
if (soPatKind > 0.5 && soPatKind < 1.5) {             // STRIPES along one axis
  soAlb = mix(soAlb, vSoColor2, so_aastep(0.5, fract(vSoPat.z)));
} else if (soPatKind > 1.5 && soPatKind < 2.5) {      // DOTS
  vec2 cell = fract(vec2(vSoPat.y + floor(vSoPat.z) * 0.5, vSoPat.z)) - 0.5;
  float d = length(cell);
  float w = max(fwidth(d), 1e-3);
  soAlb = mix(soAlb, vSoColor2, 1.0 - smoothstep(0.22 - w, 0.22 + w, d));
} else if (soPatKind > 3.5 && soPatKind < 4.5) {      // STARS
  vec2 cell = fract(vec2(vSoPat.y + floor(vSoPat.z) * 0.5, vSoPat.z)) - 0.5;
  float d = so_sdStar5(cell * 2.4, 0.55, 0.42);
  float w = max(fwidth(d), 1e-3);
  soAlb = mix(soAlb, vSoColor2, 1.0 - smoothstep(-w, w, d));
} else if (soPatKind > 4.5 && soPatKind < 5.5) {      // WEDGES around the vertical axis
  soAlb = mix(soAlb, vSoColor2, so_aastep(0.5, fract(vSoPat.y * 0.5)));
} else if (soPatKind > 5.5 && soPatKind < 6.5) {      // BAND: a flat ring with inked borders
  float bt = vSoPat.y * 0.5 + 0.5;
  float e = min(bt, 1.0 - bt);
  float ew = max(fwidth(e), 1e-3);
  soAlb = mix(vec3(${hexVec(INK)}), soAlb, smoothstep(0.17 - ew, 0.17 + ew, e));
} else if (soPatKind > 6.5) {                         // GLOW_BAND: a neon tube, hot core to deep rim
  float bt = vSoPat.y * 0.5 + 0.5;
  float core = 1.0 - abs(bt * 2.0 - 1.0);
  soAlb = mix(soAlb * 0.55, mix(soAlb, vec3(1.0, 0.85, 0.75), 0.45), smoothstep(0.2, 0.95, core));
  float e = min(bt, 1.0 - bt);
  float ew = max(fwidth(e), 1e-3);
  soAlb = mix(vec3(${hexVec(INK)}), soAlb, smoothstep(0.06 - ew, 0.06 + ew, e));
}
bool soSolid = vSoInfo.w > 0.5 || (soMode > 6.5 && soMode < 8.5);
bool soGlass = soMode > 9.5 && soMode < 10.5;
if (uGhost > 0.5 && !soSolid) {
  // §12.4 ghost: identity-tinted, unlit + fresnel, 4×4 ordered dither at 0.55 (eyes, the face and
  // the pebble-ready rock stay solid).
  soAlpha = max(0.55, soFaceA);
  if (soMode > 0.5) soAlb = uGhostColor;
  soUnlit = 1.0;
}
if (soGlass || (uGhost > 0.5 && !soSolid) || uFade > 0.0) {
  float fres = 1.0 - abs(dot(normalize(vNormal), normalize(vViewPosition)));
  float a = soGlass && uGhost < 0.5 ? vSoPat.y + pow(fres, 3.0) * 1.1 : soAlpha + fres * 0.3;
  a *= 1.0 - uFade;
  if (so_bayer4(gl_FragCoord.xy) >= a) discard;
  if (soGlass) { soAlb = mix(soAlb, vec3(1.0), fres * fres); soUnlit = 1.0; }
}
diffuseColor.rgb = soAlb;
`;

const MAIN_OUTPUT = /* glsl */ `
outgoingLight += so_glossRim(normal, geometryViewDir, soGlossK, 0.35, uRimColor);
outgoingLight = mix(outgoingLight, max(outgoingLight, soAlb * 0.97), soEyeLift);
// Character fill floor: on dark maps (pinball's indigo hemisphere) the shadow side would sink to
// mud; it never drops below 36% of the albedo, while bright maps are untouched.
outgoingLight = max(outgoingLight, soAlb * 0.36);
outgoingLight += soEmit;
if (soUnlit > 0.5) outgoingLight = soAlb;
if (uGhost > 0.5) {
  float fres = 1.0 - abs(dot(normal, geometryViewDir));
  outgoingLight += uGhostColor * pow(fres, 2.0) * 0.9;
}
outgoingLight = mix(outgoingLight, vec3(1.0), uFlash);
#include <opaque_fragment>`;

const AURA_VERTEX = /* glsl */ `
uniform float uAuraWidth;
attribute vec4 aInfo;
attribute vec3 aOutlineNormal;
varying vec3 vN;
varying vec3 vV;
#include <common>
#include <skinning_pars_vertex>
void main() {
  vec3 objectNormal = dot(aOutlineNormal, aOutlineNormal) > 0.25 ? aOutlineNormal : normal;
  ${SKIN1.base}
  ${SKIN1.normal}
  vec3 transformed = position;
  ${SKIN1.vertex}
  vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
  vN = normalize(normalMatrix * objectNormal);
  mvPosition.xyz += vN * uAuraWidth;
  vV = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
  if (aInfo.y < 0.001) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
}`;

const AURA_FRAGMENT = /* glsl */ `
uniform vec3 uGhostColor;
uniform float uTime;
varying vec3 vN;
varying vec3 vV;
void main() {
  float a = smoothstep(0.0, 0.8, -dot(normalize(vN), normalize(vV)));
  float shimmer = 0.85 + 0.15 * sin(uTime * 3.0);
  gl_FragColor = vec4(uGhostColor * a * 0.5 * shimmer, 1.0);
}`;

function hexVec(hex: string): string {
  const c = new Color(hex);
  return `${c.r.toFixed(4)}, ${c.g.toFixed(4)}, ${c.b.toFixed(4)}`;
}

export function createPartUniforms(face: boolean): PartUniforms {
  return {
    body: { value: new Color('#FF5A4E') },
    body2: { value: new Color('#C8281E') },
    pattern: { value: 0 },
    ident: { value: new Color('#FF5A4E') },
    team: { value: new Color('#FF5A4E') },
    expr: { value: [0, 0, 0] },
    face: { value: face ? 1 : 0 },
    blush: { value: 0.5 },
    flush: { value: 0 },
    flash: { value: 0 },
    spiral: { value: 0 },
    danger: { value: 0 },
    potato: { value: 0 },
    ghost: { value: 0 },
    ghostColor: { value: new Color('#FFFFFF') },
    glyph: { value: 0 },
    gloss: { value: 0.55 },
    spark: { value: 0 },
    seed: { value: 0 },
    fade: { value: 0 },
  };
}

interface SharedPartMaterials { hulls: Map<number, ShaderMaterial>; depth: MeshDepthMaterial }
const sharedByLib = new WeakMap<MaterialLibrary, SharedPartMaterials>();

function sharedFor(lib: MaterialLibrary): SharedPartMaterials {
  let s = sharedByLib.get(lib);
  if (!s) {
    const depth = new MeshDepthMaterial({ depthPacking: RGBADepthPacking });
    depth.onBeforeCompile = (shader) => {
      shader.vertexShader = singleBoneSkinning(shader.vertexShader)
        .replace('#include <common>', '#include <common>\nattribute vec4 aInfo;')
        .replace('#include <project_vertex>', '#include <project_vertex>\nif (aInfo.z < 0.5) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);');
    };
    depth.customProgramCacheKey = () => 'so-part-depth-1';
    s = { hulls: new Map(), depth };
    sharedByLib.set(lib, s);
  }
  return s;
}

/**
 * The hull material for part meshes of width `width` (meters). Shared by every rig and prop of the
 * library; it is the library's own `outlineMaterial`, which understands skinning and hull weights.
 */
export function partHullMaterial(lib: MaterialLibrary, width: number): ShaderMaterial {
  return lib.outlineMaterial(width) as ShaderMaterial;
}

/** The shadow-map depth material for part meshes (unlit decor does not cast). */
export function partDepthMaterial(lib: MaterialLibrary): MeshDepthMaterial {
  return sharedFor(lib).depth;
}

/** Builds the per-instance materials of one part mesh around a fresh uniform set. */
export function createPartMaterials(lib: MaterialLibrary, mouthAtlas: Texture | null): PartMaterials {
  const shared = libraryShared(lib);
  const u = createPartUniforms(mouthAtlas !== null);
  const key = `so-part-v4:${mouthAtlas ? 1 : 0}`;

  const main = new MeshToonMaterial({ vertexColors: true, gradientMap: lib.rampTexture });
  if (mouthAtlas) main.defines = { SO_MOUTH: '' };
  main.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, {
      uRimColor: shared.rimColor, uTime: shared.time,
      uBody: u.body, uBody2: u.body2, uPattern: u.pattern, uIdent: u.ident, uTeam: u.team, uExpr: u.expr,
      uFace: u.face, uBlush: u.blush, uFlush: u.flush, uFlash: u.flash, uSpiral: u.spiral, uDanger: u.danger,
      uPotato: u.potato, uGhost: u.ghost, uGhostColor: u.ghostColor, uGlyph: u.glyph, uGloss: u.gloss, uSpark: u.spark,
      uSeed: u.seed, uFade: u.fade, uMouth: { value: mouthAtlas },
    });
    shader.vertexShader = singleBoneSkinning(shader.vertexShader)
      .replace('#include <common>', `#include <common>\n${VERTEX_DECL}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
vSoObj = position; vSoInfo = aInfo; vSoPat = aPat; vSoColor2 = so_unpackColor(aPat.x);`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAGMENT_DECL}`)
      .replace('#include <lights_pars_begin>', `#include <lights_pars_begin>\n${GLOSS_GLSL}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${MAIN_COLOR}`)
      .replace('#include <opaque_fragment>', MAIN_OUTPUT);
  };
  main.customProgramCacheKey = () => key;

  const aura = new ShaderMaterial({
    uniforms: { uGhostColor: u.ghostColor, uAuraWidth: { value: 0.12 }, uTime: shared.time },
    vertexShader: AURA_VERTEX,
    fragmentShader: AURA_FRAGMENT,
    side: BackSide,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    toneMapped: false,
  });

  return {
    main, aura, u,
    dispose() {
      main.dispose();
      aura.dispose();
    },
  };
}
